import React, { useEffect } from 'react'
import { Feedback } from '@chegg-wt/core-wa/types'
import { SentenceOffset } from '@chegg-wt/core-wa/state/types'
import { FeedbackTab } from '@chegg-wt/core-wa/constants/paraphraser'
import { LoadingIcon } from '@chegg-ui/horizon-icons'
import {
  isPaperCheckInProgressSelector,
  fetchNewSentencesLoadingSelector,
  paperIdSelector,
  toastSelector,
  paperRequiresRecheckSelector,
  wordCountSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { useSelector } from 'react-redux'
import { getFeedbacksByCategory, getTabList } from './utils'
import { LoaderContainer, Tabs, TabsContainer } from './styled'
import {
  SUGGESTION_TABS_ARIA_LABEL,
  SUGGESTION_TABS_ID,
  SUGGESTION_TABS_TEST,
} from './constants'

interface SuggestionTabsInterface {
  feedbacks: Feedback[]
  sentenceOffsets: SentenceOffset[]
  activeTab: FeedbackTab
  setActiveTab: (tabIndex: number) => void
}

const SuggestionTabs: React.FC<SuggestionTabsInterface> = ({
  feedbacks,
  sentenceOffsets,
  activeTab,
  setActiveTab,
}) => {
  const { grammarFeedbacks, paraphraseFeedbacks } = getFeedbacksByCategory(
    feedbacks,
    sentenceOffsets
  )
  const isFeedbackListEmpty = feedbacks.length === 0
  const paperId = useSelector(paperIdSelector)
  const requiresRecheck = useSelector(paperRequiresRecheckSelector)
  const isAllFeedbacksAcceptedState =
    useSelector(wordCountSelector) > 0 && isFeedbackListEmpty && !requiresRecheck

  const isFetchingNewSentences = useSelector(fetchNewSentencesLoadingSelector)
  const handleSectionTabIndex = (index: number) => {
    setActiveTab(index)
  }

  useEffect(() => {
    if (feedbacks.length > 0 && activeTab === FeedbackTab.NO_ACTIVE_TAB) {
      handleSectionTabIndex(FeedbackTab.GRAMMAR_TAB) // always activate grammar tab first
    }
  }, [feedbacks])

  useEffect(() => {
    if (!isFeedbackListEmpty) return
    setActiveTab(
      requiresRecheck || !paperId ? FeedbackTab.NO_ACTIVE_TAB : FeedbackTab.GRAMMAR_TAB
    )
  }, [requiresRecheck, isFeedbackListEmpty, paperId])

  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const toastType = useSelector(toastSelector)

  return (
    <TabsContainer>
      {isFetchingNewSentences && (
        <LoaderContainer>
          <LoadingIcon />
        </LoaderContainer>
      )}
      <Tabs
        tabs={getTabList(
          activeTab,
          grammarFeedbacks,
          paraphraseFeedbacks,
          isPaperCheckInProgress,
          toastType,
          isAllFeedbacksAcceptedState
        )}
        activeTabIndex={activeTab}
        id={SUGGESTION_TABS_ID}
        disabled={isFeedbackListEmpty}
        onChangeIndex={(index) => handleSectionTabIndex(index)}
        aria-label={SUGGESTION_TABS_ARIA_LABEL}
        data-test={SUGGESTION_TABS_TEST}
        grammarCardsCount={grammarFeedbacks.length}
        clarityCardsCount={paraphraseFeedbacks.length}
        paperId={paperId}
        isPaperCheckInProgress={isPaperCheckInProgress}
        toastType={toastType}
        requiresRecheck={requiresRecheck}
      />
    </TabsContainer>
  )
}

export default SuggestionTabs
